// Tipos y utilidades compartidas de Marketing · Contactos (listas, segmentos, timeline).
import type { JSX } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, ApiError } from "../../../lib/api.ts";
import { getCountry } from "../../../lib/country.ts";
import { toast } from "../../../components/toast.tsx";

/** Criterios de segmento (mismo esquema que la API: lib/mkt-segment.ts). */
export interface Criteria {
  q?: string | undefined;
  tags?: string[] | undefined;
  tagsMode?: "any" | "all" | undefined;
  excludeTags?: string[] | undefined;
  statuses?: string[] | undefined;
  scoreMin?: number | undefined;
  scoreMax?: number | undefined;
  countries?: Array<"CO" | "PE"> | undefined;
  ownerIds?: string[] | undefined;
  accountIds?: string[] | undefined;
  accountQ?: string | undefined;
  createdFrom?: string | undefined;
  createdTo?: string | undefined;
  hasEmail?: boolean | undefined;
  custom?: Array<{ key: string; value: string }> | undefined;
  leadSources?: string[] | undefined;
  neverContacted?: boolean | undefined;
}
export const NO_STATUS = "__none__";

export interface MktContact {
  id: string; firstName: string | null; lastName: string; email: string | null; phone: string | null; mobile: string | null;
  title: string | null; accountId: string | null; accountName: string | null; mktStatus: string | null; mktScore: number;
  ownerId: string; ownerName: string; countryCode: "CO" | "PE"; leadSource: string | null; createdAt: string;
  unsubscribed: boolean; tags: string[];
}
export interface MktList {
  id: string; name: string; description: string | null; kind: "static" | "dynamic"; source: string;
  members: number; withEmail: number; createdAt: string; createdByName: string | null; criteria?: Criteria | null;
}
export interface Options {
  owners: Array<{ id: string; name: string }>;
  statuses: string[];
  leadSources: string[];
  tags: string[];
  customKeys: string[];
  lists: Array<{ id: string; name: string; kind: string }>;
}

export function useMktOptions() {
  return useQuery<Options>({ queryKey: ["mkt", "contact-options"], queryFn: () => api.get("/mkt/contacts/options"), staleTime: 60_000 });
}

/** Quita claves vacías para enviar/guardar un criterio limpio. */
export function cleanCriteria(c: Criteria): Criteria {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(c)) {
    if (v == null || v === "" || (Array.isArray(v) && v.length === 0)) continue;
    if (k === "tagsMode" && !c.tags?.length) continue;
    out[k] = v;
  }
  return out as Criteria;
}
export const criteriaCount = (c: Criteria): number => Object.keys(cleanCriteria({ ...c, q: undefined, tagsMode: undefined })).length;

/** Resumen legible de un criterio (para la tarjeta del segmento). */
export function describeCriteria(c: Criteria | null | undefined, opts?: Options): string[] {
  if (!c) return [];
  const owner = (id: string): string => opts?.owners.find((o) => o.id === id)?.name ?? "usuario";
  const out: string[] = [];
  if (c.q) out.push(`Texto: "${c.q}"`);
  if (c.tags?.length) out.push(`Etiquetas (${c.tagsMode === "all" ? "todas" : "alguna"}): ${c.tags.join(", ")}`);
  if (c.excludeTags?.length) out.push(`Sin etiquetas: ${c.excludeTags.join(", ")}`);
  if (c.statuses?.length) out.push(`Estado: ${c.statuses.map((s) => (s === NO_STATUS ? "sin estado" : s)).join(", ")}`);
  if (c.scoreMin != null || c.scoreMax != null) out.push(`Puntaje: ${c.scoreMin ?? "−∞"} a ${c.scoreMax ?? "∞"}`);
  if (c.countries?.length) out.push(`País: ${c.countries.join(", ")}`);
  if (c.ownerIds?.length) out.push(`Propietario: ${c.ownerIds.map(owner).join(", ")}`);
  if (c.accountQ) out.push(`Empresa contiene "${c.accountQ}"`);
  if (c.createdFrom || c.createdTo) out.push(`Creado: ${c.createdFrom ?? "…"} → ${c.createdTo ?? "…"}`);
  if (c.hasEmail != null) out.push(c.hasEmail ? "Con email" : "Sin email");
  for (const cf of c.custom ?? []) out.push(`${cf.key} = ${cf.value}`);
  if (c.leadSources?.length) out.push(`Origen: ${c.leadSources.join(", ")}`);
  if (c.neverContacted) out.push("Nunca contactados");
  return out;
}

// ────────── Estado de marketing (badge) ──────────
const STATUS_STYLE: Array<[RegExp, string]> = [
  [/fr[ií]o/i, "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200"],
  [/tibio/i, "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"],
  [/caliente/i, "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200"],
  [/oportunidad/i, "bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200"],
  [/cliente/i, "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200"],
  [/perdid|descartad/i, "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200"],
];
export function StatusBadge({ status }: { status: string | null | undefined }): JSX.Element {
  if (!status) return <span className="text-ink-muted text-xs">—</span>;
  const cls = STATUS_STYLE.find(([re]) => re.test(status))?.[1] ?? "bg-surface-hover text-ink-secondary";
  return <span className={`badge ${cls} whitespace-nowrap`}>{status}</span>;
}

/** "hace 3 días", "hace 2 meses"… */
export function relTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const diff = Date.now() - t;
  const fut = diff < 0;
  const s = Math.abs(diff) / 1000;
  const fmt = (n: number, u: string, us: string): string => { const v = Math.floor(n); const w = v === 1 ? u : us; return fut ? `en ${v} ${w}` : `hace ${v} ${w}`; };
  if (s < 60) return fut ? "en instantes" : "hace instantes";
  if (s < 3600) return fmt(s / 60, "minuto", "minutos");
  if (s < 86400) return fmt(s / 3600, "hora", "horas");
  if (s < 86400 * 30) return fmt(s / 86400, "día", "días");
  if (s < 86400 * 365) return fmt(s / (86400 * 30), "mes", "meses");
  return fmt(s / (86400 * 365), "año", "años");
}

export const fullName = (c: { firstName: string | null; lastName: string }): string =>
  `${c.firstName ?? ""} ${c.lastName === "-" ? "" : c.lastName}`.trim() || "(sin nombre)";

/** Descarga un archivo de la API (envía cookie y país activo). */
export async function downloadFile(path: string, filename: string, body?: unknown): Promise<void> {
  try {
    const res = await fetch(`/api/v1${path}`, {
      method: body === undefined ? "GET" : "POST",
      credentials: "include",
      headers: { "X-Country": getCountry(), ...(body === undefined ? {} : { "Content-Type": "application/json" }) },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
    if (!res.ok) {
      let msg = "No se pudo exportar.";
      try { const j = (await res.json()) as { message?: string }; if (j.message) msg = j.message; } catch { /* sin cuerpo */ }
      throw new ApiError(res.status, "export_failed", msg);
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  } catch (e) {
    toast.error(e instanceof ApiError ? e.message : "No se pudo exportar.");
  }
}

export const errMsg = (e: unknown, fallback: string): string => (e instanceof ApiError ? e.message : fallback);
